import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getOneTimeContractById, uploadFiles, getCurrentUser, createInvoiceForOneTimeContract, downloadFile, downloadAutentiDocument, withdrawOneTimeContract, markOneTimeContractAsPaid, markOneTimeContractAsReadyToPayout, markOneTimeContractAsReadyToSignature, markOneTimeContractAsNeedChange } from '@/service/weexpertService';
import { toast } from '@/components/ui/use-toast';
import { GET_COUNTRY_NAME_BY_CODE } from '@/constants';
import { getDecodedFileName } from '@/lib/utils';
import FileUploader from '@/components/global/FileUploader';
import AdditionalFeePopup from '@/components/popups/one-time-contract.additional-fee.popup';
import ProceedToPaymentPopup from '@/components/popups/one-time-contract.proceed-payment.popup';
import NeedChangePopup from '@/components/popups/one-time-contract.need-change.popup';
import LanguageSelectorForPdfDowloadNew from '@/components/popups/invoice.pdf.download.popup';
import axios from 'axios';
import { DownloadIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const OneTimeContractViewPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCopyrightUpload, setShowCopyrightUpload] = useState(false);
  const [selectedSignedCopyrightFiles, setSelectedSignedCopyrightFiles] = useState([]);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [showMarkAsPaidDialog, setShowMarkAsPaidDialog] = useState(false);
  const [showMarkAsReadyToPayoutDialog, setShowMarkAsReadyToPayoutDialog] = useState(false);
  const [showMarkAsReadyToSignatureDialog, setShowMarkAsReadyToSignatureDialog] = useState(false);

  // Fetch contract data
  const { data: contractData, isLoading, error } = useQuery({
    queryKey: ['oneTimeContract', id],
    queryFn: () => getOneTimeContractById(id),
    retry: false,
    refetchOnWindowFocus: false
  });

  // Fetch current user data
  const { data: userData } = useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    retry: false
  });


  // Create invoice mutation
  const createInvoiceMutation = useMutation({
    mutationFn: () => createInvoiceForOneTimeContract(id),
    onSuccess: () => {
      toast({
        title: t('alerts:succes'),
        description: t('contracts:one_time_contract_invoice_created_description'),
      });
      queryClient.invalidateQueries(['oneTimeContract', id]);
    },
    onError: (error) => {
      toast({
        title: t('alerts:error'),
        description: error.response?.data?.message || t('alerts:one_time_contract_invoice_creation_failed'),
        variant: "destructive",
      });
    }
  });

  // Withdraw contract mutation
  const withdrawMutation = useMutation({
    mutationFn: () => withdrawOneTimeContract(id),
    onSuccess: () => {
      toast({
        title: t('alerts:succes'),
        description: t('contracts:one_time_contract_withdrawn_successfully'),
      });
      setShowWithdrawDialog(false);
      queryClient.invalidateQueries(['oneTimeContract', id]);
    },
    onError: (error) => {
      toast({
        title: t('alerts:error'),
        description: error.response?.data?.message || t('alerts:one_time_contract_withdrawal_failed'),
        variant: "destructive",
      });
    }
  });

  // Mark as paid mutation
  const markAsPaidMutation = useMutation({
    mutationFn: () => markOneTimeContractAsPaid(id),
    onSuccess: () => {
      toast({
        title: t('alerts:succes'),
        description: t('contracts:one_time_contract_marked_paid'),
      });
      setShowMarkAsPaidDialog(false);
      queryClient.invalidateQueries(['oneTimeContract', id]);
    },
    onError: (error) => {
      toast({
        title: t('alerts:error'),
        description: error.response?.data?.message || t('alerts:one_time_contract_mark_paid_failed'),
        variant: "destructive",
      });
    }
  });

  // Mark as ready to payout mutation
  const markAsReadyToPayoutMutation = useMutation({
    mutationFn: () => markOneTimeContractAsReadyToPayout(id),
    onSuccess: () => {
      toast({
        title: t('alerts:succes'),
        description: t('contracts:one_time_contract_marked_ready_payout'),
      });
      setShowMarkAsReadyToPayoutDialog(false);
      queryClient.invalidateQueries(['oneTimeContract', id]);
    },
    onError: (error) => {
      toast({
        title: t('alerts:error'),
        description: error.response?.data?.message || t('alerts:one_time_contract_mark_ready_payout_failed'),
        variant: "destructive",
      });
    }
  });

  // Mark as ready to signature mutation
  const markAsReadyToSignatureMutation = useMutation({
    mutationFn: () => markOneTimeContractAsReadyToSignature(id),
    onSuccess: () => {
      toast({
        title: t('alerts:succes'),
        description: t('contracts:one_time_contract_signature_request_submitted'),
      });
      setShowMarkAsReadyToSignatureDialog(false);
      queryClient.invalidateQueries(['oneTimeContract', id]);
    },
    onError: (error) => {
      toast({
        title: t('alerts:error'),
        description: error.response?.data?.message || t('alerts:one_time_contract_signature_request_failed'),
        variant: "destructive",
      });
    }
  });

  // Upload signed copyright document mutation
  const uploadSignedCopyrightMutation = useMutation({
    mutationFn: async (files) => {
      // First upload the file
      const uploadResponse = await uploadFiles(files);
      const fileId = uploadResponse.data.files[0].id;

      // Then attach it as signed copyright document
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:8080';
      return axios.post(`${serverUrl}/one-time-contracts/${id}/signed-copyright`, {
        fileId: fileId
      });
    },
    onSuccess: () => {
      toast({
        title: t('alerts:succes'),
        description: t('contracts:one_time_contract_signed_copyright_uploaded'),
      });
      setShowCopyrightUpload(false);
      setSelectedSignedCopyrightFiles([]);
      queryClient.invalidateQueries(['oneTimeContract', id]);
    },
    onError: (error) => {
      toast({
        title: t('alerts:error'),
        description: error.response?.data?.message || t('alerts:one_time_contract_document_upload_failed'),
        variant: "destructive",
      });
    }
  });

  // Handle loading state
  if (isLoading) {
    return (
      <div className="w-full px-8 py-10 bg-grayLightMainBg">
        <div className="text-4xl mb-8">{t('contracts:one_time_contract_title')}</div>
        <Card className="p-6">
          <div className="text-center">{t('common:loading')}</div>
        </Card>
      </div>
    );
  }

  // Handle error state
  if (error || !contractData?.data?.contract) {
    return (
      <div className="w-full px-8 py-10 bg-grayLightMainBg">
        <div className="text-4xl mb-8">{t('contracts:one_time_contract_title')}</div>
        <Card className="p-6">
          <div className="text-center">
            <p className="text-red-500 mb-4">{t('alerts:one_time_contract_loading_error')}</p>
            <Button
              onClick={() => navigate('/contracts/one-time')}
              variant="outline"
            >
              {t('common:back_to_list')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const contract = contractData.data.contract;

  // Helper function to find document signature for a specific entity
  const findDocumentSignature = (entityType, entityId) => {
    if (!contract.documentSignatures || !Array.isArray(contract.documentSignatures)) {
      return null;
    }
    return contract.documentSignatures.find(
      sig => sig.entityType === entityType && sig.entityId === entityId && sig.signatureStatus == "COMPLETED"
    );
  };

  // Handler for downloading Autenti document
  const handleDownloadAutentiDocument = async (autentiDocumentId) => {
    try {
      await downloadAutentiDocument(autentiDocumentId);
      toast({
        title: t('alerts:succes'),
        description: t('contracts:one_time_contract_document_downloaded'),
      });
    } catch (error) {
      toast({
        title: t('alerts:error'),
        description: t('alerts:one_time_contract_document_download_failed'),
        variant: "destructive",
      });
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'DRAFT':
        return { label: t('contracts:draft_status'), variant: 'secondary' };
      case 'AWAITING_APPROVAL':
        return { label: t('contracts:awaiting_approval_status'), variant: 'warning' };
      case 'ACTIVE':
        return { label: t('contracts:active_feminine'), variant: 'default' };
      case 'COMPLETED':
        return { label: t('contracts:completed_feminine'), variant: 'outline' };
      case 'AWATING_PAYMENT':
        return { label: t('contracts:awaiting_payment_status'), variant: 'destructive' };
      case 'AWATING_SIGNATURE':
        return { label: t('contracts:awaiting_signature_status'), variant: 'destructive' };
      case 'STARTED_MONEY_WITHDRAW_PROCESS':
        return { label: t('contracts:withdraw_process_started_status'), variant: 'warning' };
      case 'MONEY_SEND_TO_USED':
        return { label: t('contracts:money_sent_status'), variant: 'success' };
      case 'AWAITNG_USER_PAYOUT_REQUEST':
        return { label: t('contracts:awaiting_payout_request_status'), variant: 'warning' };
      case 'READY_FOR_SIGNATURE':
        return { label: t('contracts:ready_for_signature_status'), variant: 'warning' };
      default:
        return { label: status, variant: 'secondary' };
    }
  };

  const statusDisplay = getStatusDisplay(contract.status);

  return (
    <div className="w-full px-8 py-10 bg-grayLightMainBg">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="text-4xl">
          {t('contracts:contract_title_with_number', { number: contract.contractNumber })}
        </div>
        <div className="flex gap-2">
          {userData && userData.data.roles !== 'CLIENT' && !contract.contract && !contract.bill && (
            <ProceedToPaymentPopup
              contractId={id}
              contractCreatedAt={contract.createdAt}
              onSuccess={() => queryClient.invalidateQueries(['oneTimeContract', id])}
            />
          )}
          {userData && ['ASSISTANT', 'ADMIN', 'HEAD_OF_DEPARTMENT', 'SUPER_ADMIN'].includes(userData.data.roles) &&
           ['DRAFT', 'AWAITING_APPROVAL'].includes(contract.status) && !contract.proforma && (
            <Button
              onClick={() => createInvoiceMutation.mutate()}
              disabled={createInvoiceMutation.isLoading}
              variant="outline"
            >
              {createInvoiceMutation.isLoading ? t('common:creating_loading') : t('contracts:create_invoice_button')}
            </Button>
          )}
          {['AWAITING_APPROVAL', "WAITING_FOR_CHANGE"].includes(contract.status) && userData && (
            (userData.data.roles === 'CLIENT' && contract.client?.id === userData.data.id) ||
            ['ASSISTANT', 'ADMIN', 'HEAD_OF_DEPARTMENT', 'SUPER_ADMIN'].includes(userData.data.roles)
          ) && (
            <Button
              onClick={() => navigate(`/contracts/one-time/${id}/edit`)}
            >
              {t('common:button_change')}
            </Button>
          )}
          {userData && ['ASSISTANT', 'ADMIN', 'HEAD_OF_DEPARTMENT', 'SUPER_ADMIN'].includes(userData.data.roles) &&
           contract.status === 'AWATING_PAYMENT' && (
            <>
              <Button
                onClick={() => setShowMarkAsReadyToPayoutDialog(true)}
                variant="outline"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {t('contracts:mark_ready_for_payout_button')}
              </Button>
              <AdditionalFeePopup
                contractId={id}
                onSuccess={() => queryClient.invalidateQueries(['oneTimeContract', id])}
              />
            </>
          )}
          {userData && ['ADMIN', 'HEAD_OF_DEPARTMENT', 'SUPER_ADMIN'].includes(userData.data.roles) &&
           contract.contract && contract.bill && (
            <Button
              onClick={() => setShowWithdrawDialog(true)}
              variant="destructive"
            >
              {t('contracts:withdraw_contract_button')}
            </Button>
          )}
          {userData && ['ASSISTANT', 'ADMIN', 'HEAD_OF_DEPARTMENT', 'SUPER_ADMIN'].includes(userData.data.roles) &&
           contract.status === 'STARTED_MONEY_WITHDRAW_PROCESS' && (
            <Button
              onClick={() => setShowMarkAsPaidDialog(true)}
              variant="default"
              className="bg-green-600 hover:bg-green-700"
            >
              {t('contracts:mark_as_paid_button')}
            </Button>
          )}
          {userData && userData.data.roles === 'CLIENT' &&
           contract.status === 'AWAITNG_USER_PAYOUT_REQUEST' &&
           contract.client?.id === userData.data.id && (
            <Button
              onClick={() => setShowMarkAsReadyToSignatureDialog(true)}
              variant="default"
            >
              {t('contracts:submit_signature_request_button')}
            </Button>
          )}
          {userData && ['ASSISTANT', 'ADMIN', 'HEAD_OF_DEPARTMENT', 'SUPER_ADMIN'].includes(userData.data.roles) &&
           ['DRAFT', 'AWAITING_APPROVAL', 'ACTIVE'].includes(contract.status) && (
            <NeedChangePopup
              contractId={id}
              onSuccess={() => queryClient.invalidateQueries(['oneTimeContract', id])}
            />
          )}
          <Button
            onClick={() => navigate('/contracts/one-time')}
            variant="outline"
          >
            {t('common:back_to_list')}
          </Button>
        </div>
      </div>

      <div className="w-full space-y-8">
        <Card className="p-6">
          <h3 className="text-xl font-medium mb-4">{t('contracts:contract_details_title')}</h3>
          <div className="mb-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:title_label')}</label>
              <h2 className="text-2xl font-medium text-gray-900">{contract.title}</h2>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:description_label')}</label>
              <p className="text-gray-600">{contract.description}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className='grid grid-cols-1 '>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:net_price')}</label>
                <p className="text-2xl font-bold text-green-600">{contract.priceNetto} {contract.currency}</p>
              </div>
              {contract.fee !== null && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:weexpert_fee_label')}</label>
                <p className="text-lg font-semibold text-blue-600">{contract.fee} {contract.currency}</p>
              </div>
              )}
              {contract.pit !== null && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:pit_label')}</label>
                  <p className="text-lg font-semibold text-blue-600">{contract.pit} {contract.currency}</p>
                </div>
              )}
            {(contract.additionalFeeAmount !== null || contract.additionalFeeComment) && (
              <div>
                <div className="">
                  <label className="block text-sm font-medium text-gray-700 ">{t('contracts:additional_fee_label')}</label>
                  {contract.additionalFeeAmount !== null && (
                    <p className="text-lg font-semibold text-orange-600 mb-2">
                      {contract.additionalFeeAmount} {contract.currency}
                    </p>
                  )}
                  {contract.additionalFeeComment && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('common:comment_label')}</label>
                      <p className="text-sm text-gray-700 mt-1">{contract.additionalFeeComment}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
                {contract.bankAccount && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:bank_account_label')}</label>
                <p className="text-gray-900 font-mono text-sm">{contract.bankAccount}</p>
              </div>
            )}
               <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:payment_due_date_label')}</label>
              <p className="text-gray-900">{format(new Date(contract.paymentDueDate), 'dd.MM.yyyy')}</p>
            </div>
             <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:created_at')}</label>
              <p className="text-gray-900">{format(new Date(contract.createdAt), 'dd.MM.yyyy HH:mm')}</p>
            </div>
              {contract.updatedAt && contract.updatedAt !== contract.createdAt && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:last_modified')}</label>
                <p className="text-gray-900">{format(new Date(contract.updatedAt), 'dd.MM.yyyy HH:mm')}</p>
              </div>
            )}
            </div>
            <div className='grid grid-cols-1 gap-6'>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:status')}</label>
              <Badge variant={statusDisplay.variant}>
                {statusDisplay.label}
              </Badge>
            </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:service_category_label')}</label>
                <Badge>{contract.serviceCategory}</Badge>
              </div>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:service_subcategory_label')}</label>
              <Badge variant="outline">{contract.serviceSubcategory}</Badge>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:copyright_type_label')}</label>
              <Badge variant="secondary">{contract.copyrightType}</Badge>
            </div>
            {contract.proforma && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-sm font-medium text-gray-700">{t('contracts:proforma_label')}</label>
                  <LanguageSelectorForPdfDowloadNew
                    className="h-6 px-2 py-0 text-xs"
                    icon={<DownloadIcon className="h-3 w-3" />}
                    selectedItems={[contract.proforma.id]}
                  />
                </div>
                <Link
                  to={`/admin/invoices/details/${contract.proforma.id}`}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {contract.proforma.invoiceNumber || contract.proforma.invoiceId || `Faktura #${contract.proforma.id}`}
                </Link>
              </div>
            )}
            {contract.invoice && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-sm font-medium text-gray-700">{t('contracts:invoice_label')}</label>
                  <LanguageSelectorForPdfDowloadNew
                    className="h-6 px-2 py-0 text-xs"
                    icon={<DownloadIcon className="h-3 w-3" />}
                    selectedItems={[contract.invoice.id]}
                  />
                </div>
                <Link
                  to={`/admin/invoices/details/${contract.invoice.id}`}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {contract.invoice.invoiceNumber || contract.invoice.invoiceId || `Faktura #${contract.invoice.id}`}
                </Link>
              </div>
            )}
            {contract.contract && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-sm font-medium text-gray-700">{t('contracts:contract_label')}</label>
                  {(() => {
                    const contractSignature = findDocumentSignature('Contract', contract.contract.id);
                    return contractSignature && contractSignature.autentiDocumentId ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none"
                        onClick={() => handleDownloadAutentiDocument(contractSignature.autentiDocumentId)}
                        title="Pobierz podpisany dokument"
                      >
                        <DownloadIcon className="h-3 w-3" />
                        PDF
                      </Button>
                    ) : null;
                  })()}
                </div>
                <Link
                  to={`/contracts/${contract.contract.id}`}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {contract.contract.contractId || `Umowa #${contract.contract.id}`}
                </Link>
              </div>
            )}
            {contract.bill && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-sm font-medium text-gray-700">{t('contracts:bill_label')}</label>
                  {(() => {
                    const billSignature = findDocumentSignature('Bill', contract.bill.id);
                    return billSignature && billSignature.autentiDocumentId ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className=" h-6 px-2 text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none"
                        onClick={() => handleDownloadAutentiDocument(billSignature.autentiDocumentId)}
                        title="Pobierz podpisany dokument"
                      >
                        <DownloadIcon className="h-3 w-3" />
                        PDF
                      </Button>
                    ) : null;
                  })()}
                </div>
                <Link
                  to={`/bill/view/${contract.bill.id}`}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {contract.bill.billId || `Rachunek #${contract.bill.id}`}
                </Link>
              </div>
            )}
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="text-xl font-medium mb-4">{t('contracts:client_section_title')}</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:full_name_label')}</label>
              <p className="text-gray-900">{contract.client ? `${contract.client.firstName} ${contract.client.lastName}` : '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:email_label')}</label>
              <p className="text-gray-900">{contract.client?.email || '-'}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <h3 className="text-xl font-medium mb-4">{t('contracts:counterparty_data_title')}</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:name')}</label>
              <p className="text-gray-900">{contract.counterparty?.name || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:type')}</label>
              <p className="text-gray-900">
                {contract.counterparty?.counterpartyType === 'COMPANY' ? t('common:company_type_company') : t('common:company_type_individual')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:address')}</label>
              <p className="text-gray-900">{contract.counterparty?.address || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:city')}</label>
              <p className="text-gray-900">{contract.counterparty?.city || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:post_index')}</label>
              <p className="text-gray-900">{contract.counterparty?.postIndex || '-'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:country')}</label>
              <p className="text-gray-900">{contract.counterparty?.country ? GET_COUNTRY_NAME_BY_CODE(contract.counterparty.country) : '-'}</p>
            </div>
            {contract.counterparty?.NIP && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:nip_label')}</label>
                <p className="text-gray-900">{contract.counterparty.NIP}</p>
              </div>
            )}
            {contract.counterparty?.email && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:email_label')}</label>
                <p className="text-gray-900">{contract.counterparty.email}</p>
              </div>
            )}
          </div>
        </Card>

        {(contract.messageForCounterparty || contract.counterpartyEmail) && (
          <Card className="p-6">
            <h3 className="text-xl font-medium mb-4">{t('contracts:message_for_counterparty_title')}</h3>
            <div className="space-y-4">
              {contract.counterpartyEmail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('contracts:counterparty_email_label')}</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{contract.counterpartyEmail}</p>
                </div>
              )}
              {contract.messageForCounterparty && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common:message_content_label')}</label>
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                    <p className="text-gray-900 whitespace-pre-wrap">{contract.messageForCounterparty}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {contract.workFiles && contract.workFiles.length > 0 && (
          <Card className="p-6">
            <h3 className="text-xl font-medium mb-4">{t('contracts:work_files_label', { count: contract.workFiles.length })}</h3>
            <div className="space-y-2">
              {contract.workFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <span className="text-sm font-medium">{getDecodedFileName(file.path)}</span>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('common:added_at_label')}: {format(new Date(file.createdAt), 'dd.MM.yyyy HH:mm')}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadFile(file.id)}
                  >
                    {t('common:button_download')}
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

      </div>

      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('contracts:confirm_withdraw_contract_title')}</DialogTitle>
            <DialogDescription>
              {t('contracts:confirm_withdraw_contract_description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowWithdrawDialog(false)}
              disabled={withdrawMutation.isLoading}
            >
              {t('contracts:cancel_button')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => withdrawMutation.mutate()}
              disabled={withdrawMutation.isLoading}
            >
              {withdrawMutation.isLoading ? t('common:withdrawing_loading') : t('contracts:withdraw_contract_button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMarkAsPaidDialog} onOpenChange={setShowMarkAsPaidDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('contracts:confirm_mark_as_paid_title')}</DialogTitle>
            <DialogDescription>
              {t('contracts:confirm_mark_as_paid_description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMarkAsPaidDialog(false)}
              disabled={markAsPaidMutation.isLoading}
            >
              {t('contracts:cancel_button')}
            </Button>
            <Button
              variant="default"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => markAsPaidMutation.mutate()}
              disabled={markAsPaidMutation.isLoading}
            >
              {markAsPaidMutation.isLoading ? t('common:processing_loading') : t('contracts:mark_as_paid_button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMarkAsReadyToPayoutDialog} onOpenChange={setShowMarkAsReadyToPayoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('contracts:confirm_mark_ready_payout_title')}</DialogTitle>
            <DialogDescription>
              {t('contracts:confirm_mark_ready_payout_description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMarkAsReadyToPayoutDialog(false)}
              disabled={markAsReadyToPayoutMutation.isLoading}
            >
              {t('contracts:cancel_button')}
            </Button>
            <Button
              variant="default"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => markAsReadyToPayoutMutation.mutate()}
              disabled={markAsReadyToPayoutMutation.isLoading}
            >
              {markAsReadyToPayoutMutation.isLoading ? t('common:processing_loading') : t('contracts:mark_ready_for_payout_button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMarkAsReadyToSignatureDialog} onOpenChange={setShowMarkAsReadyToSignatureDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('contracts:submit_signature_request_dialog_title')}</DialogTitle>
            <DialogDescription>
              {t('contracts:submit_signature_request_dialog_description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowMarkAsReadyToSignatureDialog(false)}
              disabled={markAsReadyToSignatureMutation.isLoading}
            >
              {t('contracts:cancel_button')}
            </Button>
            <Button
              variant="default"
              onClick={() => markAsReadyToSignatureMutation.mutate()}
              disabled={markAsReadyToSignatureMutation.isLoading}
            >
              {markAsReadyToSignatureMutation.isLoading ? t('common:processing_loading') : t('common:submit_request_button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OneTimeContractViewPage;
